/**
 * The backend serializes C# enums as their integer index (e.g. `"status": 0`),
 * while the frontend models them as string enums. The declaration order of our
 * TypeScript string enums matches the 0-based C# values, so a numeric value can
 * be mapped back to its string name via the enum's value list. Strings (and
 * already-correct values) pass through unchanged.
 */
export function enumName<T extends Record<string, string>>(e: T, value: unknown): T[keyof T] {
  if (typeof value === 'number') {
    return (Object.values(e)[value] ?? String(value)) as T[keyof T];
  }
  return (value ?? '') as T[keyof T];
}

/**
 * Inverse of {@link enumName}: the backend deserializes enums from their integer
 * index, so outgoing payloads must send the numeric value rather than the string
 * name. Maps a string enum value to its 0-based declaration index; numbers pass
 * through and unknown/empty values become `undefined` (so they can be omitted).
 */
export function enumIndex<T extends Record<string, string>>(e: T, value: unknown): number | undefined {
  if (typeof value === 'number') return value;
  if (value == null || value === '') return undefined;
  const idx = Object.values(e).indexOf(value as string);
  return idx >= 0 ? idx : undefined;
}
